export type ConfidenceLevel = 'high' | 'medium' | 'low';

export interface ClassificationRule {
  pattern: RegExp;
  categoryId: string;
  confidence: ConfidenceLevel;
}

const CREDIT_CARD_PAYMENT_PATTERNS = [
  /^BMOVIL\.PAGO TDC/i,
  /^PAGO TDC/i,
  /^PAGO TARJETA DE CREDITO/i,
  /^PAGO TC\b/i,
];

export function isCreditCardPayment(description: string): boolean {
  return CREDIT_CARD_PAYMENT_PATTERNS.some((pattern) =>
    pattern.test(description)
  );
}

export interface ClassificationResult {
  categoryId: string;
  confidence: ConfidenceLevel;
  needsConfirmation: boolean;
  matchedRule?: string;
}

const CLASSIFICATION_RULES: ClassificationRule[] = [
  { pattern: /^NETFLIX/i, categoryId: 'streaming', confidence: 'high' },
  {
    pattern: /^SPOTIFY|PAYPAL\s*\*\s*SPOTIFY|PAY\s*PAL\s*\*\s*SPOTIFY/i,
    categoryId: 'streaming',
    confidence: 'high',
  },
  { pattern: /^HBO/i, categoryId: 'streaming', confidence: 'high' },
  { pattern: /^DISNEY/i, categoryId: 'streaming', confidence: 'high' },
  { pattern: /^AMAZON PRIME/i, categoryId: 'streaming', confidence: 'high' },
  // Trabajo (servers, AI subscriptions, work tools)
  { pattern: /^CLAUDE\.AI/i, categoryId: 'trabajo', confidence: 'high' },
  {
    pattern: /^OPENAI\s*\*\s*CHATGPT/i,
    categoryId: 'trabajo',
    confidence: 'high',
  },
  { pattern: /^LINODE|AKAMAI/i, categoryId: 'trabajo', confidence: 'high' },
  { pattern: /^TWILIO/i, categoryId: 'trabajo', confidence: 'high' },
  {
    pattern: /^PAYPAL\s*\*\s*HERE\s+NA\s+LLC/i,
    categoryId: 'trabajo',
    confidence: 'high',
  },

  // Software (general subscriptions)
  {
    pattern:
      /^Google\s+(One|YouTubePremium|minimalist|Chess|GuitarTuna|The New Yorker)/i,
    categoryId: 'otras-suscripciones',
    confidence: 'high',
  },
  {
    pattern: /^EZVIZLIFE\.COM|PAYPAL\s*\*\s*EZVIZ/i,
    categoryId: 'software',
    confidence: 'high',
  },
  { pattern: /^TOTAL PLAY/i, categoryId: 'internet', confidence: 'high' },
  { pattern: /^LASTPASS/i, categoryId: 'software', confidence: 'high' },

  { pattern: /^CFE CONTIGO/i, categoryId: 'luz', confidence: 'high' },
  { pattern: /^JAPAMA/i, categoryId: 'agua', confidence: 'high' },
  {
    pattern: /^TELCEL|STR\*TELCEL|STRIPE\s*\*\s*TELCEL/i,
    categoryId: 'telefono',
    confidence: 'high',
  },
  {
    pattern: /^MUNICIPIO DE AHOME/i,
    categoryId: 'otros-servicios',
    confidence: 'high',
  },
  {
    pattern: /^MERPAGO\*TANIASANCHEZA/i,
    categoryId: 'otros-servicios',
    confidence: 'high',
  },
  {
    pattern: /^PAYCLIP\*DEPTO HEYWORKX/i,
    categoryId: 'otros-servicios',
    confidence: 'high',
  },

  // Impuestos
  {
    pattern: /^SRIA\s+(ADMON|FINAN)/i,
    categoryId: 'impuestos',
    confidence: 'high',
  },

  { pattern: /^INST PIAGET/i, categoryId: 'colegiaturas', confidence: 'high' },

  // Libros
  {
    pattern: /^LIB\s+\w|^PAYCLIP\*LIB\s+LIBROS/i,
    categoryId: 'libros',
    confidence: 'high',
  },
  {
    pattern: /^MERPAGO\*(LACRAFTE|LAPICEROROJO)/i,
    categoryId: 'libros',
    confidence: 'high',
  },

  {
    pattern: /^UBER EATS|STR\*UBER EATS/i,
    categoryId: 'delivery',
    confidence: 'high',
  },
  { pattern: /^STARBUCKS/i, categoryId: 'restaurantes', confidence: 'high' },
  {
    pattern: /^REST\s|RESTAURANT/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  { pattern: /^LA YARDITA/i, categoryId: 'supermercado', confidence: 'high' },
  { pattern: /^MARISCOS/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^MC DONALD/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^CARLS JR/i, categoryId: 'restaurantes', confidence: 'high' },
  {
    pattern: /^HASHIIRA SUSHI/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^DULCE DE LECHE/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  { pattern: /^PAPIZZAS/i, categoryId: 'restaurantes', confidence: 'high' },
  {
    pattern: /^DON GATO TAQUERIA/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^APOLOBURGUER|APOLO BURGUER/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^CAFET\s|CAFE\s|CAFFENIO/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^PANADERIA|PASTEL PANAMA/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^POLLOS SANCHEZ|CASA DEL POLLO/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^HELADOS DOLPHY/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  { pattern: /^NACHOS PLAZA/i, categoryId: 'restaurantes', confidence: 'high' },
  {
    pattern: /^VANEBANANA BAKERY/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^MAISON\s+(BAKERY|THE)/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  { pattern: /^312 CAFE/i, categoryId: 'restaurantes', confidence: 'high' },
  // More restaurants
  { pattern: /^ALIMENTOS/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^DOLCE\s+PL/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^F DE FRESA/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^ISHINOKA/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^JUICE LAAB/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^MAR MARISCOS/i, categoryId: 'restaurantes', confidence: 'high' },
  { pattern: /^REY DE REYES/i, categoryId: 'restaurantes', confidence: 'high' },
  {
    pattern: /^ZTL\*BELWAFFEL/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^WUZIPAY\*AZUCAR/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern:
      /^MERPAGO\*(BARRIOLATINO|COCINAPARAISO|COTACOSTEAKLM|DDL|HDVALAMEDA|HOTPORK|OTTROCAFE)/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },
  {
    pattern: /^PAYCLIP\*REST|^CLIP\s*MX\*REST/i,
    categoryId: 'restaurantes',
    confidence: 'high',
  },

  {
    pattern: /^SAM\s*S\s*CLUB/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  {
    pattern: /^SUPERCENTER|SUPER\s+LOS\s+MOCHIS/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  { pattern: /^WAL\s*MART/i, categoryId: 'supermercado', confidence: 'high' },
  {
    pattern: /^LEY\s+(EXPRESS|COUNTRY)/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  { pattern: /^COSTCO/i, categoryId: 'supermercado', confidence: 'high' },
  { pattern: /^OXXO/i, categoryId: 'supermercado', confidence: 'high' },
  {
    pattern: /^SUPER KIOSKO|SUPER KIOSCO/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  { pattern: /^ABARROTES/i, categoryId: 'supermercado', confidence: 'high' },
  {
    pattern: /^LA CASA DEL QUESO/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  { pattern: /^CARNICERIA/i, categoryId: 'supermercado', confidence: 'high' },
  { pattern: /^TERRAPESCA/i, categoryId: 'supermercado', confidence: 'high' },
  {
    pattern: /^MERPAGO\*AGROFRESASPA/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  // More supermarkets
  { pattern: /^ABARR\s/i, categoryId: 'supermercado', confidence: 'high' },
  { pattern: /^ABTS SUPER/i, categoryId: 'supermercado', confidence: 'high' },
  {
    pattern: /^E COMMERCE COSTCO/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  { pattern: /^SORIANA/i, categoryId: 'supermercado', confidence: 'high' },
  {
    pattern: /^SUPER CONQUISTADORES/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  {
    pattern: /^TIEN SUPER KIOSKO/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  {
    pattern: /^APT MOCHIS AEROMARKET/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },
  {
    pattern: /^PAYCLIP\*DULC LAS PERSH/i,
    categoryId: 'supermercado',
    confidence: 'high',
  },

  {
    pattern: /^AMAZON(?!\s*PRIME)/i,
    categoryId: 'otras-compras',
    confidence: 'high',
  },
  { pattern: /^LIVERPOOL/i, categoryId: 'otras-compras', confidence: 'high' },
  { pattern: /^SEARS/i, categoryId: 'otras-compras', confidence: 'high' },
  { pattern: /^SUBURBIA/i, categoryId: 'otras-compras', confidence: 'high' },
  { pattern: /^HOME DEPOT/i, categoryId: 'hogar', confidence: 'high' },
  {
    pattern: /^CALZZAPATO|NETPAY\s*\*\s*CALZZAPATO/i,
    categoryId: 'ropa',
    confidence: 'high',
  },
  { pattern: /^PALLETS VARSOVIA/i, categoryId: 'ropa', confidence: 'high' },
  {
    pattern: /^PAYPAL\s*\*\s*TRUE CLASSIC/i,
    categoryId: 'ropa',
    confidence: 'high',
  },
  {
    pattern: /^AUTOZONE/i,
    categoryId: 'mantenimiento',
    confidence: 'high',
  },
  { pattern: /^STEREN/i, categoryId: 'electronica', confidence: 'high' },
  { pattern: /^FERR\s|FERRETERIA/i, categoryId: 'hogar', confidence: 'high' },
  { pattern: /^DISFRACES/i, categoryId: 'otras-compras', confidence: 'high' },
  { pattern: /^PAPELERIA/i, categoryId: 'otras-compras', confidence: 'high' },
  {
    pattern: /^DULC MAT PRIM/i,
    categoryId: 'otras-compras',
    confidence: 'high',
  },
  {
    pattern: /^MERPAGO\*TIENDACHINA/i,
    categoryId: 'otras-compras',
    confidence: 'high',
  },
  {
    pattern: /^PAYPAL\s*\*\s*(GIVEBUTTER|WIKIPEDIA)/i,
    categoryId: 'otras-compras',
    confidence: 'high',
  },
  { pattern: /^ROPA PROMO/i, categoryId: 'ropa', confidence: 'high' },

  { pattern: /^PMX\*GAS|PEMEX/i, categoryId: 'gasolina', confidence: 'high' },
  { pattern: /^GASPASA/i, categoryId: 'gasolina', confidence: 'high' },
  { pattern: /^GAS\s+SEVAFUSA/i, categoryId: 'gasolina', confidence: 'high' },
  {
    pattern: /^ESTACION\s|GASOLINA/i,
    categoryId: 'gasolina',
    confidence: 'high',
  },
  {
    pattern: /^GAS\s+LA\s+PIEDRERA/i,
    categoryId: 'gasolina',
    confidence: 'high',
  },
  { pattern: /^GAS\s+JIQUILPAN/i, categoryId: 'gasolina', confidence: 'high' },
  { pattern: /^GASOL/i, categoryId: 'gasolina', confidence: 'high' },
  { pattern: /^GAS SERV/i, categoryId: 'gasolina', confidence: 'high' },
  { pattern: /^GASO CENTENARIO/i, categoryId: 'gasolina', confidence: 'high' },
  {
    pattern: /^UBER(?!\s*EATS)/i,
    categoryId: 'uber-taxi',
    confidence: 'high',
  },
  {
    pattern: /^GECKO AUTOLAVADOS/i,
    categoryId: 'mantenimiento',
    confidence: 'high',
  },
  {
    pattern: /^BPK\*REFAC TOYOTA|^TOYOTA LOS MOCHIS/i,
    categoryId: 'mantenimiento',
    confidence: 'high',
  },

  { pattern: /^HOSP\s|HOSPITAL/i, categoryId: 'medico', confidence: 'high' },
  { pattern: /^DR\s|DOCTOR/i, categoryId: 'medico', confidence: 'high' },
  { pattern: /^NUTRIOLOGA/i, categoryId: 'medico', confidence: 'high' },
  {
    pattern: /^F\s*AHORRO|FARM\s|FAR\s+GUAD/i,
    categoryId: 'farmacia',
    confidence: 'high',
  },
  { pattern: /^GIMNASIO|GYM/i, categoryId: 'gimnasio', confidence: 'high' },
  {
    pattern: /^CLUB DEP ROCA FIT/i,
    categoryId: 'gimnasio',
    confidence: 'high',
  },
  {
    pattern: /^PILATES|STRIPE\s*\*\s*EMBRACE\s*PILATE/i,
    categoryId: 'gimnasio',
    confidence: 'high',
  },
  // Suplementos
  {
    pattern: /^BULLIES NUTRITION|^FAMILYFITBYBULLIES/i,
    categoryId: 'suplementos',
    confidence: 'high',
  },
  {
    pattern: /^PAYPAL\s*\*\s*HABITOSNUTR/i,
    categoryId: 'suplementos',
    confidence: 'high',
  },

  { pattern: /^CINEMEX/i, categoryId: 'cine', confidence: 'high' },
  {
    pattern: /^PAYPAL\s*\*\s*NINTENDO/i,
    categoryId: 'videojuegos',
    confidence: 'high',
  },

  // Salidas (family outings, activities)
  {
    pattern: /^MUSEO/i,
    categoryId: 'salidas',
    confidence: 'high',
  },
  {
    pattern: /^JUMPING CENTER|JUMSPOT/i,
    categoryId: 'salidas',
    confidence: 'high',
  },
  {
    pattern: /^BOLETOMOVIL|CONEKTA\*BOLETOMOVIL/i,
    categoryId: 'salidas',
    confidence: 'high',
  },
  {
    pattern: /^PAY\s*PAL\s*\*\s*GRUPORECREA|^PAYPAL\s*\*\s*GRUPORECREA/i,
    categoryId: 'salidas',
    confidence: 'high',
  },
  { pattern: /^DEMUCEC/i, categoryId: 'salidas', confidence: 'high' },
  {
    pattern: /^MERPAGO\*MUNDOIDEAL/i,
    categoryId: 'salidas',
    confidence: 'high',
  },

  {
    pattern: /^PET CENTER/i,
    categoryId: 'tienda-de-mascotas',
    confidence: 'high',
  },
  {
    pattern: /^VET\s|VETERINAR/i,
    categoryId: 'veterinario',
    confidence: 'high',
  },

  { pattern: /^QUALITAS/i, categoryId: 'auto', confidence: 'high' },
  { pattern: /^GNP PORTAL/i, categoryId: 'gastos-medicos', confidence: 'high' },

  {
    pattern: CREDIT_CARD_PAYMENT_PATTERNS[0],
    categoryId: 'transferencias',
    confidence: 'high',
  },
  {
    pattern: /^ABONO FINANC/i,
    categoryId: 'transferencias',
    confidence: 'high',
  },
  {
    pattern: /^ABONO POR TRASP/i,
    categoryId: 'transferencias',
    confidence: 'high',
  },
  {
    pattern: /^ALTA PARA MESES/i,
    categoryId: 'transferencias',
    confidence: 'high',
  },
  {
    pattern: /^CONTRATACION BENEFICIOS/i,
    categoryId: 'transferencias',
    confidence: 'high',
  },
  {
    pattern: /^ADMINISTRACION TARJ|^BONIF/i,
    categoryId: 'transferencias',
    confidence: 'high',
  },

  {
    pattern: /\b(FOOD|CAFE|COFFEE|PIZZA|BURGER|TACO)\b/i,
    categoryId: 'restaurantes',
    confidence: 'medium',
  },
  {
    pattern: /\b(SHOP|STORE|RETAIL|TIENDA)\b/i,
    categoryId: 'otras-compras',
    confidence: 'medium',
  },
  {
    pattern: /\b(FARMACIA|PHARMACY)\b/i,
    categoryId: 'farmacia',
    confidence: 'medium',
  },
  {
    pattern: /\b(MERCADO|MARKET)\b/i,
    categoryId: 'supermercado',
    confidence: 'medium',
  },
];

export function classify(description: string): ClassificationResult {
  for (const rule of CLASSIFICATION_RULES) {
    if (rule.pattern.test(description)) {
      return {
        categoryId: rule.categoryId,
        confidence: rule.confidence,
        needsConfirmation: rule.confidence !== 'high',
        matchedRule: rule.pattern.toString(),
      };
    }
  }
  return {
    categoryId: 'sin-categoria',
    confidence: 'low',
    needsConfirmation: true,
  };
}

export function classifyBatch(
  descriptions: string[]
): Map<string, ClassificationResult> {
  const results = new Map<string, ClassificationResult>();
  for (const description of descriptions) {
    results.set(description, classify(description));
  }
  return results;
}

export function getClassificationRules(): ReadonlyArray<
  Readonly<ClassificationRule>
> {
  return CLASSIFICATION_RULES;
}

export interface ClassifiedTransaction {
  description: string;
  categoryId: string;
  classificationConfidence: ConfidenceLevel;
  needsConfirmation: boolean;
}

export function classifyTransaction<
  T extends { description: string; categoryId?: string | null },
>(transaction: T): T & ClassifiedTransaction {
  const result = classify(transaction.description);
  return {
    ...transaction,
    categoryId: result.categoryId,
    classificationConfidence: result.confidence,
    needsConfirmation: result.needsConfirmation,
  };
}

export function classifyTransactions<
  T extends { description: string; categoryId?: string | null },
>(transactions: T[]): (T & ClassifiedTransaction)[] {
  return transactions.map(classifyTransaction);
}
